import { IonIcon } from "@ionic/react";
import { ReactElement } from "react";
import styled from "styled-components";
import { resolveRichContentIcon } from "../richContentIcons";
import { safeHref } from "../safeUrl";

interface IconLinkItem {
  icon?: string;
  /** Uploaded custom icon (bucket URL) — wins over the named icon when present. */
  iconUrl?: string;
  label?: string;
  url?: string;
}

interface IconLinksData {
  items?: IconLinkItem[];
}

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px 10px;
`;

// Whole item is the tap target (a11y: label is the accessible name; the icon is
// decorative). Sized for thumbs: ≥48px circle + label below.
const Item = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 84px;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.6;
  }
`;

const Circle = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--app-color-accent-rgb, 45, 182, 180), 0.12);
`;

const Glyph = styled(IonIcon)`
  font-size: 26px;
  color: var(--app-color-accent, #2db6b4);
`;

const CustomGlyph = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

const Label = styled.span`
  font-size: 13px;
  line-height: 1.25;
  text-align: center;
  color: var(--app-color-text, #041723);
  overflow-wrap: anywhere;
`;

const IconLinksBlock = ({ data }: { data: IconLinksData }): ReactElement | null => {
  const items = (data.items ?? []).filter((item) => item.label || item.url);
  if (!items.length) return null;
  return (
    <Row>
      {items.map((item, i) => {
        const href = safeHref(item.url);
        return (
          <Item
            key={i}
            href={href}
            as={href ? "a" : "div"}
            target={href?.startsWith("https://") ? "_blank" : undefined}
            rel={href ? "noopener noreferrer" : undefined}
          >
            <Circle aria-hidden="true">
              {item.iconUrl ? (
                <CustomGlyph src={item.iconUrl} alt="" />
              ) : (
                <Glyph icon={resolveRichContentIcon(item.icon)} />
              )}
            </Circle>
            <Label>{item.label}</Label>
          </Item>
        );
      })}
    </Row>
  );
};

export default IconLinksBlock;
